import React, { useState, useEffect } from 'react';
import { Row, Col, message } from 'antd';
import { financeService, ProfitSummary, ProfitTrendPoint, ValidRankingItem } from '../../services/financeService';
import dayjs from 'dayjs';
import { FinanceFilters } from './components/FinanceFilters';
import { ProfitStats } from './components/ProfitStats';
import { ProfitTrendChart } from './components/ProfitTrendChart';
import { ProfitRankingTable } from './components/ProfitRankingTable';

const FinanceDashboard: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState<ProfitSummary | null>(null);
    const [trendData, setTrendData] = useState<ProfitTrendPoint[]>([]);
    const [rankingData, setRankingData] = useState<ValidRankingItem[]>([]);

    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
        dayjs().subtract(30, 'days'),
        dayjs(),
    ]);
    const [granularity, setGranularity] = useState<'day' | 'week' | 'month'>('day');

    useEffect(() => { fetchData(); }, [dateRange, granularity]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {
                start_date: dateRange[0].format('YYYY-MM-DD'),
                end_date: dateRange[1].format('YYYY-MM-DD'),
            };

            const [summaryRes, trendRes, rankingRes] = await Promise.all([
                financeService.getProfitSummary(params),
                financeService.getProfitTrend({ ...params, granularity }),
                financeService.getProfitRanking({ ...params, limit: 10 }),
            ]);

            setSummary(summaryRes.data);
            setTrendData(trendRes.data);
            setRankingData(rankingRes.data);
        } catch (error) {
            console.error('Fetch finance data failed', error);
            message.error('加载数据失败');
        } finally { setLoading(false); }
    };

    const [recalculating, setRecalculating] = useState(false);

    const handleRecalculate = async () => {
        setRecalculating(true);
        try {
            await financeService.recalculateProfit({
                start_date: dateRange[0].format('YYYY-MM-DD'),
                end_date: dateRange[1].format('YYYY-MM-DD'),
            });
            message.success('重算任务已提交');
            fetchData();
        } catch (error) {
            message.error('重算请求失败');
        } finally { setRecalculating(false); }
    };

    return (
        <div style={{ padding: 24 }}>
            <FinanceFilters
                dateRange={dateRange}
                setDateRange={setDateRange}
                granularity={granularity}
                setGranularity={setGranularity}
                onRefresh={fetchData}
                onRecalculate={handleRecalculate}
                recalculating={recalculating}
            />

            <ProfitStats summary={summary} />

            <Row gutter={24}>
                <Col span={14}>
                    <ProfitTrendChart trendData={trendData} loading={loading} />
                </Col>
                <Col span={10}>
                    <ProfitRankingTable rankingData={rankingData} loading={loading} />
                </Col>
            </Row>
        </div>
    );
};

export default FinanceDashboard;
